import React from 'react';

/**
 * Formatters for different value types.
 * Uses Indian locale (en-IN) for number formatting.
 */
const formatters = {
  currency: (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(value);
  },
  percent: (value) => `${value}%`,
  number: (value) => new Intl.NumberFormat('en-IN').format(value),
};

/**
 * WtfDataColumn - Renders a WTF-sourced value in a table cell with source indication.
 * Handles loading states (skeleton), null values, and staleness indicator.
 *
 * @param {number|string|null} value - The WTF value to display
 * @param {boolean} loading - Show skeleton if loading
 * @param {boolean} stale - Show stale indicator (amber dot)
 * @param {string} format - Formatting: "number", "currency", or "percent" (default "number")
 * @param {string} className - Additional CSS classes
 */
export function WtfDataColumn({ value, loading, stale, format = "number", className = "" }) {
  // Loading skeleton
  if (loading) {
    return (
      <div className="h-4 w-12 bg-gray-200 animate-pulse rounded" />
    );
  }

  // Null/undefined value
  if (value === null || value === undefined) {
    return (
      <span className="text-gray-400" title="Source: WTF API">
        --
      </span>
    );
  }

  // Format the value
  const formatter = formatters[format] || formatters.number;
  const formattedValue = formatter(value);

  return (
    <span
      className={`inline-flex items-center gap-1 ${className}`}
      title="Source: WTF API"
    >
      {formattedValue}
      {stale && (
        <span className="h-1.5 w-1.5 rounded-full bg-amber-500 inline-block flex-shrink-0" />
      )}
    </span>
  );
}
